import { UnauthenticatedError } from "../../errors";
/** Error code in a 401 body signalling the Firebase ID token has expired and should be refreshed. */
export const AUTH_TOKEN_EXPIRED = "AUTH_TOKEN_EXPIRED";
/**
 * True when an axios error is a 401 whose body is `{ error: { code: "AUTH_TOKEN_EXPIRED" } }`,
 * i.e. the request's Firebase ID token has expired and a refresh + retry is worth attempting.
 */
export function isAuthTokenExpiredError(err) {
    if (typeof err !== "object" || err === null || !("response" in err)) {
        return false;
    }
    const { response } = err;
    if (!response || response.status !== 401) {
        return false;
    }
    const code = response.data?.error?.code;
    return code === AUTH_TOKEN_EXPIRED;
}
/** Fetch-response equivalent of {@link isAuthTokenExpiredError}. Reads a clone so the caller can still consume the body. */
export async function isAuthTokenExpiredResponse(response) {
    if (response.status !== 401) {
        return false;
    }
    try {
        const body = (await response.clone().json());
        return body?.error?.code === AUTH_TOKEN_EXPIRED;
    }
    catch {
        return false;
    }
}
export class AgentSessionNotFoundError extends Error {
    /**
     * @param id: id of missing agent session.
     */
    constructor(id) {
        super();
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AgentSessionNotFoundError);
        }
        this.name = "AgentSessionNotFoundError";
        this.message = `Agent session not found${id ? ` for id: '${id}'` : "."}`;
    }
}
export class AgentSessionBadRequest extends Error {
    /**
     * @param message: optional error message.
     */
    constructor(message) {
        super();
        this.name = "AgentSessionBadRequest";
        this.message = message || "Bad request.";
    }
}
export class AgentSessionsFetchFailedError extends Error {
    /**
     * @param message: optional error message.
     */
    constructor(id) {
        super();
        this.name = "AgentSessionsFetchFailedError";
        this.message = `Failed to fetch agent session${id ? ` for id: '${id}'` : "."}`;
    }
}
export class AgentSessionCreationFailedError extends Error {
    /**
     * @param message: optional error message.
     */
    constructor(message) {
        super();
        this.name = "AgentSessionCreationFailedError";
        this.message = message || "Failed to create agent session.";
    }
}
export class AgentSessionServerError extends Error {
    /**
     * @param message: optional error message.
     */
    constructor(message) {
        super();
        this.name = "AgentSessionServerError";
        this.message = message || "Issue processing agent session.";
    }
}
/**
 * Handles errors from agent session API requests and throws appropriate custom errors based on the response status code.
 * @param err The error object thrown by axios or the Fetch API.
 * @param id Optional session ID related to the error, used for more specific error messages.
 * @param defaultError Optional fallback error to throw when the status code is not handled or the error shape is unexpected.
 * Defaults to the original error (`err`) if not provided.
 * @throws {UnauthenticatedError} If the response status is 401.
 * @throws {AgentSessionBadRequest} If the response status is 400.
 * @throws {AgentSessionNotFoundError} If the response status is 404, with optional session ID in the message.
 * @throws {AgentSessionServerError} If the response status is any 5xx (500–599) server error.
 * @throws defaultError for all other cases, including unhandled status codes or non-object errors.
 */
export function handleAgentSessionRequestError(err, { id, defaultError = err } = {}) {
    let statusCode = null;
    if (typeof err !== "object" || err === null) {
        throw defaultError;
    }
    if ("response" in err &&
        typeof err.response === "object" &&
        err.response !== null &&
        "status" in err.response &&
        typeof err.response.status === "number") {
        statusCode = err.response?.status;
    }
    else if ("status" in err && typeof err.status === "number") {
        statusCode = err.status;
    }
    if (statusCode && statusCode >= 500 && statusCode < 600) {
        throw new AgentSessionServerError();
    }
    switch (statusCode) {
        case 401:
            throw new UnauthenticatedError();
        case 400:
            throw new AgentSessionBadRequest();
        case 404:
            throw new AgentSessionNotFoundError(id);
    }
    throw defaultError;
}
/**
 * Reason codes for {@link AgentUploadError}.
 */
export const AgentUploadErrorReason = {
    SIGNED_URL_FAILED: "signed-url-failed",
    UPLOAD_FAILED: "upload-failed",
    EXPIRED: "expired"
};
/**
 * Error thrown when an agent file upload fails.
 */
export class AgentUploadError extends Error {
    constructor(reason, message) {
        super(message || `Agent upload failed: ${reason}`);
        this.name = "AgentUploadError";
        this.reason = reason;
    }
}
export class AgentDataParseError extends Error {
    constructor(message, originalError) {
        super(message || "Failed to parse agent data.");
        this.name = "AgentDataParseError";
        this.originalError = originalError;
    }
}
//# sourceMappingURL=errors.js.map